import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

function Metric({ label, value, unit }) {
  return (
    <div className="border border-border rounded-md p-2.5">
      <div className="overline">{label}</div>
      <div className="font-mono font-semibold text-sm mt-0.5">{value ?? "-"} <span className="text-muted-foreground text-xs">{unit}</span></div>
    </div>
  );
}

export default function BoundaryPanel({ project, reload }) {
  const id = project.id;
  const m = project.boundary_metrics;
  const [detection, setDetection] = useState(null);
  const [busy, setBusy] = useState(false);

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${id}/boundary/upload`, fd);
      if (data?.detection) setDetection(data.detection);
      if (data?.detection?.kind === "projected")
        toast.warning("Projected coordinates detected - go to the CRS step to convert to WGS 84.");
      else toast.success("Licence boundary loaded from KML");
      reload();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); e.target.value = ""; }
  };

  return (
    <div className="space-y-4">
      <label className="border-2 border-dashed border-border rounded-md p-6 flex flex-col items-center gap-2 cursor-pointer hover:border-primary transition-colors" data-testid="bd-upload-label">
        <UploadSimple size={28} className="text-muted-foreground" />
        <span className="text-sm font-medium">{busy ? "Reading boundary…" : "Upload licence boundary (KML / KMZ)"}</span>
        <span className="text-xs text-muted-foreground">The licence area is taken directly from your KML/KMZ file</span>
        <input type="file" className="hidden" accept=".kml,.kmz" onChange={uploadFile} data-testid="bd-file-input" disabled={busy} />
      </label>
      <p className="text-[11px] text-muted-foreground">
        Export the licence polygon from your GIS as KML or KMZ and upload it here. Every map and analysis is clipped strictly to this boundary.
      </p>

      {detection && (
        <div className={`text-xs rounded-md p-3 border ${detection.kind === "projected" ? "border-priority-med/50 bg-priority-med/10" : "border-priority-low/50 bg-priority-low/10"}`} data-testid="bd-detection">
          <div className="font-medium">Detected CRS: <span className="font-mono">{detection.detected_crs}</span> ({detection.kind})</div>
          <div className="text-muted-foreground mt-1">{detection.detail}</div>
          {(detection.warnings || []).map((w, i) => <div key={i} className="text-priority-med mt-1">⚠ {w}</div>)}
        </div>
      )}

      {m && (
        <div>
          <p className="overline mb-2">Boundary Metrics</p>
          <div className="grid grid-cols-2 gap-2" data-testid="bd-metrics">
            <Metric label="Area" value={m.area_ha} unit="ha" />
            <Metric label="Area" value={m.area_km2} unit="km²" />
            <Metric label="Perimeter" value={m.perimeter_km} unit="km" />
            <Metric label="UTM Zone" value={m.utm_zone} />
            <Metric label="Centre Lat" value={m.centroid?.lat} />
            <Metric label="Centre Lon" value={m.centroid?.lon} />
          </div>
        </div>
      )}
    </div>
  );
}
